// src/components/GoogleButton.tsx
import React, { useLayoutEffect, useRef, useState } from "react";
import { GoogleLogin, GoogleCredentialResponse } from "@react-oauth/google";
import { Capacitor } from "@capacitor/core";
import {
  signInWithGoogleNative,
  isGoogleCancellation,
} from "../lib/googleSignIn";
import { t } from "../i18n";

// Google's GSI button renders inside a cross-origin iframe at the exact pixel
// width passed to `width`, AND at a fixed height (40px for size="large") that
// CSS cannot override. To sit flush with the sibling Apple / email buttons —
// which are pinned to TARGET_HEIGHT — we render the GSI button at width/SCALE
// and apply transform: scale(SCALE) to a wrapper we own, so it ends up
// TARGET_HEIGHT tall and full container width. Scaling vector text + the logo
// SVG stays crisp, and the click / credential flow is completely unaffected.
const GSI_MIN_WIDTH = 240;
const GSI_MAX_WIDTH = 400;
const GSI_NATIVE_HEIGHT = 40; // height GSI renders at for size="large"
const TARGET_HEIGHT = 44; // shared height with the Apple + email buttons
const SCALE = TARGET_HEIGHT / GSI_NATIVE_HEIGHT; // 1.1

const NATIVE_LABELS: Record<string, string> = {
  signin_with: t("googleButton.signInWithGoogle"),
  signup_with: t("googleButton.signUpWithGoogle"),
  continue_with: t("googleButton.continueWithGoogle"),
};

// Official multi-color "G" — same artwork Google's own button renders.
const GoogleGLogo: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const GoogleButton: React.FC<{
  onSuccess: (credential: string) => void;
  onError?: () => void;
  text?: "signin_with" | "signup_with" | "continue_with";
  /** GSI logo placement. The fan claim stack passes "center" so the G sits
   *  next to its label exactly like the Apple/email icons (founder decision);
   *  DJ surfaces keep Google's default left G. Native ignores it — the
   *  custom button is already icon-beside-centered-text. */
  logoAlignment?: "left" | "center";
  /** The moment the button is tapped, before Google's sheet opens (onSuccess
   *  comes back seconds later). The sign-in screen notes which app-tips box
   *  was on screen then. */
  onClick?: () => void;
}> = ({ onSuccess, onError, text = "continue_with", logoAlignment = "left", onClick }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(0);
  const isNative = Capacitor.isNativePlatform();
  // Google's web button keeps the click listener it was first drawn with.
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;

  // Native app: Google blocks its web (GSI) widget inside app webviews, so we
  // run the real native Sign-in-with-Google flow instead. The idToken it
  // returns feeds the same onSuccess the web widget does — parent pages and
  // the server see no difference.
  const handleNativeClick = async () => {
    onClick?.();
    try {
      const idToken = await signInWithGoogleNative();
      if (idToken) {
        onSuccess(idToken);
      } else {
        onError?.();
      }
    } catch (err) {
      if (isGoogleCancellation(err)) return; // user closed the sheet — not an error
      console.error("Native Google Sign In error:", err);
      onError?.();
    }
  };

  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const w = Math.round(el.getBoundingClientRect().width);
    if (w <= 0) return;
    setWidth(w);
  }, []);

  // Early return AFTER the hooks (rules-of-hooks): native renders its own
  // button, so the wrapper ref never attaches and the effect is a no-op.
  if (isNative) {
    return (
      <button
        type="button"
        onClick={handleNativeClick}
        className="google-signin-btn"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "10px",
          width: "100%",
          height: TARGET_HEIGHT,
          padding: "0 20px",
          boxSizing: "border-box",
          background: "#fff",
          color: "#3c4043",
          border: "1px solid #dadce0",
          borderRadius: "8px",
          fontSize: "15px",
          fontWeight: 600,
          cursor: "pointer",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif",
        }}
      >
        <GoogleGLogo />
        {NATIVE_LABELS[text]}
      </button>
    );
  }

  // The GSI button is rendered at the container width divided by SCALE, then
  // scaled back up — so the on-screen width matches the container and the
  // height grows from 40px to TARGET_HEIGHT. Clamp to GSI's supported range.
  const gsiWidth =
    width > 0
      ? Math.min(GSI_MAX_WIDTH, Math.max(GSI_MIN_WIDTH, Math.round(width / SCALE)))
      : 0;

  return (
    <div
      ref={wrapperRef}
      className="google-btn-wrapper"
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: TARGET_HEIGHT,
      }}
    >
      {gsiWidth > 0 && (
        <div style={{ transform: `scale(${SCALE})`, transformOrigin: "center", display: "flex" }}>
          <GoogleLogin
            onSuccess={(resp: GoogleCredentialResponse) => {
              if (resp?.credential) onSuccess(resp.credential);
            }}
            onError={onError}
            click_listener={() => onClickRef.current?.()}
            useOneTap={false}
            auto_select={false}
            text={text}
            theme="outline"
            shape="rectangular"
            size="large"
            logo_alignment={logoAlignment}
            width={String(gsiWidth)}
            locale="en"
          />
        </div>
      )}
    </div>
  );
};

export default GoogleButton;
